import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Paper, Typography, CircularProgress, Box, Modal, IconButton } from '@mui/material';
import { getAuthToken, makeRequest } from '../util/AxiosInstance';
import { API_URL } from '../zustand/const';
import { handleError } from '../util/functions';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Delete } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import axios from 'axios';
import { toast } from 'sonner';

const ImageUpload = ({ setImageOrder, files, setFiles }) => {
    // Removed unused 'uploading' state
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const onDrop = useCallback(async (acceptedFiles) => {
        const newFiles = acceptedFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            uploading: false,
            retry: false,
            error: false,
            id: Math.random().toString(36).substring(7)
        }));
        setFiles(prevFiles => [...prevFiles, ...newFiles]);
        setFilesToUpload(prevFiles => [...prevFiles, ...newFiles]);
    }, [setFiles]); // Added setFiles dependency

    // Renamed upload to processUploadQueue
    const processUploadQueue = async () => {
        // Filter files that actually need uploading (not already uploaded or currently uploading)
        const filesToProcess = filesToUpload.filter(f => !f.uploadData && !f.uploading);
        if (filesToProcess.length === 0) {
            setFilesToUpload([]); // Ensure queue is cleared even if nothing to process now
            return;
        }

        const uploadPromises = filesToProcess.map(fileObj => uploadSingleFile(fileObj));

        try {
            await Promise.all(uploadPromises);
        } catch (error) {
            // Errors are handled within uploadSingleFile, but catch potential Promise.all issues
            console.error('Error processing upload batch.', error);
        } finally {
            // Clear the queue after attempting all uploads in this batch
            setFilesToUpload([]);
        }
    };

    useEffect(() => {
        // Process the queue only when files are added via onDrop
        if (filesToUpload.length > 0) {
            processUploadQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesToUpload]); // Trigger only when filesToUpload array reference changes


    useEffect(() => {
        setImageOrder(files)
        if (files.length === 0 && modalOpen) {
            closeModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, currentImageIndex, setImageOrder]); // Added setImageOrder dependency

    const deleteImage = (img) => {
        if (img) {
            let fileId = img.id
            let id = img && img.uploadData ? img.uploadData?.id : null
            if (id) {
                let currentIndex = files.findIndex(f => f.id === fileId);
                // Adjust index logic if deleting the last image in modal view
                if (modalOpen && currentIndex === currentImageIndex) {
                    if (files.length === 1) {
                        closeModal(); // Close modal if it was the last image
                    } else {
                        // Move to the previous image, wrapping around if necessary
                        setCurrentImageIndex((prev) => (prev - 1 + files.length) % files.length);
                    }
                } else if (modalOpen && currentIndex < currentImageIndex) {
                    // Adjust modal index if deleting an image before the current one
                    setCurrentImageIndex(prev => prev - 1);
                }

                setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));

                makeRequest('DELETE', `${API_URL}/file/${id}`).then(res => {
                    console.log(res);
                    toast.success("Image deleted successfully"); // Add success feedback
                }).catch(err => {
                    console.error(err);
                    handleError(err); // Show error feedback
                    // Optionally add the file back if deletion fails? Depends on desired UX
                });
            } else {
                // If image wasn't uploaded yet (no uploadData.id), just remove from state
                setFiles(prevFiles => prevFiles.filter(f => f.id !== fileId));
                toast.info("Image removed from queue");
            }
        }
    }

    // Extracted logic for uploading a single file
    const uploadSingleFile = async (fileObj) => {
        // Ensure fileObj is valid
        if (!fileObj || !fileObj.file) {
            console.error("Invalid file object passed to uploadSingleFile:", fileObj);
            return { success: false, id: fileObj?.id };
        }

        // Mark the specific file as uploading and reset error/retry state
        setFiles(prevFiles => prevFiles.map(f =>
            f.id === fileObj.id ? { ...f, uploading: true, error: false, retry: false } : f
        ));

        let formData = new FormData();
        formData.append('file', fileObj.file);

        try {
            const token = await getAuthToken();
            const axiosUpload = axios.create();

            const res = await axiosUpload.post(`${API_URL}/file`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                transformRequest: (data, headers) => {
                    if (data instanceof FormData) {
                        delete headers['Content-Type'];
                        return data;
                    }
                    return JSON.stringify(data);
                }
            });

            const uploadedImage = res.data.data;
            setFiles(prevFiles => prevFiles.map(f =>
                f.id === fileObj.id ? { ...f, uploading: false, uploadData: uploadedImage } : f
            ));
            return { success: true, id: fileObj.id };
        } catch (error) {
            console.error(`Upload failed for file ${fileObj.file.name}:`, error);
            if (error.response) {
                console.error('Server response:', error.response.data);
            }
            let err = handleError(error);
            toast.error(`Upload failed for ${fileObj.file.name}: ${err}`);
            setFiles(prevFiles => prevFiles.map(f =>
                f.id === fileObj.id ? { ...f, uploading: false, retry: true, error: true } : f
            ));
            return { success: false, id: fileObj.id };
        }
    };


    // Renamed and modified retry function
    const retryUpload = (fileToRetry) => {
        // Directly call uploadSingleFile, do not add back to queue
        uploadSingleFile(fileToRetry);
    };


    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(files);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setFiles(items);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: 'image/*', // Consider more specific types if needed: 'image/jpeg, image/png'
        multiple: true,
    });

    const openModal = (index) => {
        setCurrentImageIndex(index);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % files.length);
    const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + files.length) % files.length);

    return (
        <>
            <Paper
                className='image-upload'
                {...getRootProps()}
                sx={{
                    p: 2,
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#f0f0f0' : 'white',
                    mb: 2 // Add margin bottom for spacing
                }}
            >
                <input {...getInputProps()} />
                <Typography>Drag and drop images here, or click to select</Typography>
            </Paper>

            {files && files.length > 0 && ( // Only render DragDropContext if files exist
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="images" direction="horizontal">
                        {(provided) => (
                            <Box
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, p: 1, border: '1px solid #eee', borderRadius: 1, minHeight: '120px' }} // Add some styling
                            >
                                {files.map((file, index) => (
                                    <Draggable key={file.id || file.preview} draggableId={file.id || file.preview} index={index}>
                                        {(provided, snapshot) => ( // Added snapshot
                                            <Box
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                sx={{
                                                    width: 100,
                                                    height: 100,
                                                    m: 1,
                                                    position: 'relative',
                                                    cursor: file.uploading ? 'default' : 'pointer', // Change cursor when uploading
                                                    border: snapshot.isDragging ? '2px solid blue' : '1px solid #ddd', // Highlight when dragging
                                                    borderRadius: 1,
                                                    overflow: 'hidden', // Ensure content fits
                                                    boxShadow: snapshot.isDragging ? '0px 4px 8px rgba(0,0,0,0.2)' : 'none', // Add shadow when dragging
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#f9f9f9'
                                                }}
                                                onClick={() => !file.uploading && !file.retry && openModal(index)} // Prevent opening modal during upload/retry
                                            >
                                                <img
                                                    src={file.preview}
                                                    alt="preview"
                                                    style={{
                                                        display: 'block', // Ensure img behaves like a block
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        opacity: file.uploading || file.error ? 0.5 : 1 // Dim image during upload/error
                                                    }}
                                                />
                                                {(file.uploading || file.retry) && ( // Show overlay for uploading OR retry state
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            flexDirection: 'column', // Stack icon and progress/retry
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: file.error ? 'rgba(255,0,0,0.6)' : 'rgba(0,0,0,0.6)', // Red tint for error
                                                            color: 'white',
                                                        }}
                                                    >
                                                        {file.uploading && <CircularProgress color="inherit" size={24} />}
                                                        {file.retry && (
                                                            <IconButton
                                                                color="inherit"
                                                                onClick={(e) => {
                                                                    e.stopPropagation(); // Prevent modal opening
                                                                    retryUpload(file);
                                                                }}
                                                                title="Retry Upload" // Add tooltip
                                                            >
                                                                <ReplayIcon />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                )}
                                                {/* Optional: Add a small delete button on the corner */}
                                                {!file.uploading && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent modal opening
                                                            deleteImage(file);
                                                        }}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 2,
                                                            right: 2,
                                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                            }
                                                        }}
                                                        title="Delete Image"
                                                    >
                                                        <Delete fontSize="small" color="error" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Box>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {/* Modal remains largely the same, ensure files[currentImageIndex] exists */}
            {modalOpen && files && files.length > 0 && files[currentImageIndex] && (
                <Modal
                    open={modalOpen}
                    onClose={closeModal}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <Box
                        className='image-upload-modal'
                        sx={{
                            position: 'relative', maxWidth: '90%', maxHeight: '90%', bgcolor: 'background.paper', p: 1, boxShadow: 24, outline: 'none' // Added modal styling
                        }}>
                        <IconButton
                            color='inherit' // Use inherit for better contrast on paper
                            className='image-upload-button'
                            onClick={closeModal}
                            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }} // Style close button
                        >
                            <CloseIcon />
                        </IconButton>
                        <IconButton
                            color='inherit'
                            className='image-upload-button'
                            onClick={() => deleteImage(files[currentImageIndex])}
                            sx={{ position: 'absolute', top: 8, right: 58, zIndex: 1, bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }} // Style delete button
                        >
                            <Delete />
                        </IconButton>
                        <img
                            src={files[currentImageIndex]?.preview}
                            alt="full preview"
                            style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '85vh' }} // Adjust image style
                        />
                        {/* Navigation Buttons - only show if more than one image */}
                        {files.length > 1 && (
                            <>
                                <IconButton
                                    color='inherit'
                                    className='image-upload-button'
                                    onClick={prevImage}
                                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }} // Style nav buttons
                                >
                                    <ArrowBackIosIcon />
                                </IconButton>
                                <IconButton
                                    color='inherit'
                                    className='image-upload-button'
                                    onClick={nextImage}
                                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0, 0, 0, 0.5)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' } }} // Style nav buttons
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Modal >
            )}
        </>
    );
};

export default ImageUpload;